import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Sparkles, Loader2, Check, Building2, Users, Layout as LayoutIcon,
  Wand2, Save, Printer, FileText, Brain, Heart, Stethoscope, ShieldAlert, Search, Plus, Palette,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Header } from '../components/Header';
import { OnePagerPreview } from '../components/OnePagerPreview';
import { PreviewScaler } from '../components/PreviewScaler';
import { organizations, colorSchemes, printSizes } from '../data/catalog';
import { templates } from '../data/templates';
import { getTopicCategories } from '../data/topics';
import { printPreview } from '../lib/print';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import api from '../lib/api';

const iconMap = { Brain, Heart, Stethoscope, Sparkles, ShieldAlert };
const STEPS = ['Organization', 'Audience', 'Topic', 'Template', 'Generate', 'Customize'];

export default function Create() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { brand } = useBrand();
  const { toast } = useToast();
  const previewRef = useRef(null);
  const [step, setStep] = useState(0);
  const [org, setOrg] = useState(null);
  const [audience, setAudience] = useState('staff');
  const [topic, setTopic] = useState('');
  const [topicCategory, setTopicCategory] = useState('');
  const [customTopic, setCustomTopic] = useState('');
  const [search, setSearch] = useState('');
  const [expandedCat, setExpandedCat] = useState(null);
  const [template, setTemplate] = useState(null);
  const [policy, setPolicy] = useState('');
  const [generating, setGenerating] = useState(false);
  const [content, setContent] = useState(null);
  const [colorScheme, setColorScheme] = useState(colorSchemes[0]);
  const [printSize, setPrintSize] = useState('letter');
  const [facilityName, setFacilityName] = useState('');
  const [facilityLogo, setFacilityLogo] = useState('');
  const [saving, setSaving] = useState(false);
  const [docId, setDocId] = useState(null);

  useEffect(() => {
    if (brand) {
      if (brand.facilityName) setFacilityName((v) => v || brand.facilityName);
      if (brand.logo) setFacilityLogo((v) => v || brand.logo);
      if (brand.primaryColor) {
        setColorScheme((cs) => (cs && cs.name && cs.name !== colorSchemes[0].name) ? cs : { name: 'Brand', primary: brand.primaryColor, secondary: brand.primaryColor, background: '#F8FAFC', text: '#1E293B', accent: brand.primaryColor });
      }
    }
  }, [brand]);

  const topicCats = org ? getTopicCategories(org.id, audience) : [];

  const buildOnePager = () => ({
    id: docId,
    title: content?.title || topic,
    subtitle: content?.subtitle || '',
    content: content || {},
    colorScheme,
    layout: template?.layout || 'standard',
    docType: 'one-pager',
    templateId: template?.id,
    facilityName,
    facilityLogo,
  });

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/generate-onepager', {
        topic, category: topicCategory || 'General', layout: template.layout,
        targetAudience: audience, policyContent: policy, organization: org?.name || '',
      });
      setContent(res.data.data);
      toast({ title: 'Content generated!', description: 'Review and customize your one-pager.' });
      setStep(5);
    } catch (e) {
      toast({ title: 'Generation failed', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!user) { toast({ title: 'Sign in required', description: 'Please sign in to save.', variant: 'destructive' }); navigate('/login'); return; }
    setSaving(true);
    try {
      const payload = buildOnePager();
      let res;
      if (docId) res = await api.put(`/onepagers/${docId}`, payload);
      else res = await api.post('/onepagers', payload);
      setDocId(res.data.id);
      toast({ title: 'Saved!', description: 'Your one-pager is in your library.' });
    } catch (e) {
      toast({ title: 'Save failed', description: 'Please try again.', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setFacilityLogo(ev.target.result);
    reader.readAsDataURL(file);
  };

  const canNext = () => {
    if (step === 0) return !!org;
    if (step === 2) return !!topic;
    if (step === 3) return !!template;
    return true;
  };

  const filteredCats = topicCats.map((cat) => ({
    ...cat,
    topics: cat.topics.filter((t) => t.name.toLowerCase().includes(search.toLowerCase())),
  })).filter((cat) => cat.topics.length > 0 || !search);

  return (
    <>
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stepper */}
        <div className="flex items-center justify-between mb-6 overflow-x-auto pb-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center shrink-0">
              <button onClick={() => i < step && setStep(i)} className={`flex items-center gap-2 ${i <= step ? 'cursor-pointer' : 'cursor-default'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${i < step ? 'bg-teal-600 text-white' : i === step ? 'bg-teal-100 text-teal-700 ring-2 ring-teal-500' : 'bg-gray-100 text-gray-400'}`}>
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`text-xs font-medium hidden sm:inline ${i === step ? 'text-teal-700' : 'text-gray-500'}`}>{s}</span>
              </button>
              {i < STEPS.length - 1 && <div className="w-6 sm:w-10 h-0.5 mx-1 sm:mx-2 bg-gray-200" />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
            {step === 0 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Choose Organization</h2>
                <p className="text-gray-500 mb-6">Select the facility this material is for.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  {organizations.map((o) => (
                    <Card key={o.id} onClick={() => setOrg(o)} className={`cursor-pointer transition-all hover:shadow-md ${org?.id === o.id ? 'ring-2 ring-teal-500' : ''}`}>
                      <CardContent className="p-5 flex items-start gap-3">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: o.primaryColor }}><Building2 className="w-6 h-6 text-white" /></div>
                        <div><h3 className="font-bold text-gray-900">{o.name}</h3><p className="text-sm text-gray-500">{o.description}</p></div>
                        {org?.id === o.id && <Check className="w-5 h-5 text-teal-600 ml-auto" />}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Choose Audience</h2>
                <p className="text-gray-500 mb-6">Who is this training material for?</p>
                <div className="grid md:grid-cols-2 gap-4">
                  {[{ id: 'staff', name: 'Staff Training', desc: 'For nurses, techs, therapists & support staff', icon: Stethoscope }, { id: 'patient', name: 'Patient Education', desc: 'For patients and their families', icon: Heart }].map((a) => (
                    <Card key={a.id} onClick={() => setAudience(a.id)} className={`cursor-pointer transition-all hover:shadow-md ${audience === a.id ? 'ring-2 ring-teal-500' : ''}`}>
                      <CardContent className="p-5 flex items-start gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shrink-0"><a.icon className="w-6 h-6 text-white" /></div>
                        <div><h3 className="font-bold text-gray-900">{a.name}</h3><p className="text-sm text-gray-500">{a.desc}</p></div>
                        {audience === a.id && <Check className="w-5 h-5 text-teal-600 ml-auto" />}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Choose Topic</h2>
                <p className="text-gray-500 mb-4">Pick from the {org?.shortName} library or enter a custom topic.</p>
                <div className="relative mb-4"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input placeholder="Search topics..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" /></div>
                <Card className="bg-teal-50 border-teal-200 mb-4"><CardContent className="pt-4"><div className="flex gap-2"><Input placeholder="Enter a custom topic..." value={customTopic} onChange={(e) => setCustomTopic(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && customTopic.trim()) { setTopic(customTopic.trim()); setTopicCategory('Custom'); } }} className="flex-1" /><Button onClick={() => { if (customTopic.trim()) { setTopic(customTopic.trim()); setTopicCategory('Custom'); } }} disabled={!customTopic.trim()}><Plus className="w-4 h-4 mr-1" /> Add</Button></div>{topicCategory === 'Custom' && topic && <p className="text-xs text-teal-700 mt-2">Selected: <b>{topic}</b></p>}</CardContent></Card>
                <div className="space-y-3">
                  {filteredCats.map((cat) => {
                    const Icon = iconMap[cat.icon] || Brain;
                    const exp = expandedCat === cat.id || !!search;
                    return (
                      <Card key={cat.id} className="overflow-hidden">
                        <div className="cursor-pointer py-3 px-4 hover:bg-gray-50 flex items-center gap-3" onClick={() => setExpandedCat(exp && !search ? null : cat.id)}>
                          <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center"><Icon className="w-4 h-4 text-teal-700" /></div>
                          <div className="flex-1"><div className="text-sm font-semibold">{cat.name}</div><div className="text-xs text-muted-foreground">{cat.topics.length} topics</div></div>
                        </div>
                        {exp && (
                          <CardContent className="pt-0 pb-3"><div className="grid sm:grid-cols-2 gap-2">
                            {cat.topics.map((t) => (
                              <Button key={t.id} variant={topic === t.name ? 'default' : 'outline'} size="sm" className="justify-start" onClick={() => { setTopic(t.name); setTopicCategory(cat.name); }}>{t.name}</Button>
                            ))}
                          </div></CardContent>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Choose Template</h2>
                <p className="text-gray-500 mb-6">Pick a layout for "{topic}".</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map((t) => (
                    <Card key={t.id} onClick={() => setTemplate(t)} className={`cursor-pointer transition-all hover:-translate-y-1 hover:shadow-md ${template?.id === t.id ? 'ring-2 ring-teal-500' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2"><div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: colorScheme.primary }}><LayoutIcon className="w-4 h-4 text-white" /></div><div className="min-w-0"><div className="font-semibold text-sm truncate">{t.name}</div><div className="text-[11px] text-gray-400">{t.category}</div></div>{template?.id === t.id && <Check className="w-4 h-4 text-teal-600 ml-auto" />}</div>
                        <p className="text-xs text-gray-500">{t.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="max-w-2xl mx-auto">
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Generate Content</h2>
                <p className="text-gray-500 mb-6">AI will create your "{topic}" one-pager using the "{template?.name}" template.</p>
                <div className="mb-4">
                  <Label className="mb-1.5 block">Source Material / Policy Reference <span className="text-gray-400 font-normal">(optional)</span></Label>
                  <Textarea value={policy} onChange={(e) => setPolicy(e.target.value)} rows={6} placeholder="Paste your facility policy, procedure, or guidelines here. The AI will base the content on your text instead of general knowledge." />
                </div>
                <Button onClick={handleGenerate} disabled={generating} className="w-full h-12 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white text-base">
                  {generating ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generating with AI...</> : <><Wand2 className="w-5 h-5 mr-2" /> Generate Content</>}
                </Button>
              </div>
            )}

            {step === 5 && content && (
              <div className="grid lg:grid-cols-[1fr_360px] gap-6">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-xl font-bold text-gray-900">Preview</h2>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setStep(4)}><Wand2 className="w-4 h-4 mr-1" /> Regenerate</Button>
                      <Button variant="outline" size="sm" onClick={() => printPreview(previewRef.current, `print-size-${printSize}`)}><Printer className="w-4 h-4 mr-1" /> Print</Button>
                      <Button size="sm" onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save</Button>
                    </div>
                  </div>
                  <div className="border rounded-xl bg-gray-100 p-4 overflow-hidden">
                    <div ref={previewRef}><PreviewScaler><OnePagerPreview onePager={buildOnePager()} /></PreviewScaler></div>
                  </div>
                </div>
                <div className="space-y-4">
                  <Card><CardContent className="p-4 space-y-3">
                    <div><Label className="text-xs">Title</Label><Input value={content.title || ''} onChange={(e) => setContent({ ...content, title: e.target.value })} /></div>
                    <div><Label className="text-xs">Subtitle</Label><Input value={content.subtitle || ''} onChange={(e) => setContent({ ...content, subtitle: e.target.value })} /></div>
                    <div><Label className="text-xs">Facility Name</Label><Input value={facilityName} onChange={(e) => setFacilityName(e.target.value)} placeholder={org?.name} /></div>
                    <div><Label className="text-xs">Facility Logo</Label><Input type="file" accept="image/*" onChange={handleLogo} className="text-xs" /></div>
                  </CardContent></Card>
                  <Card><CardContent className="p-4">
                    <Label className="text-xs flex items-center gap-1 mb-2"><Palette className="w-3 h-3" /> Color Scheme</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {colorSchemes.map((c) => (
                        <button key={c.name} onClick={() => setColorScheme(c)} className={`h-9 rounded-lg border-2 ${colorScheme.name === c.name ? 'border-gray-900' : 'border-transparent'}`} style={{ backgroundColor: c.primary }} title={c.name} />
                      ))}
                    </div>
                  </CardContent></Card>
                  <Card><CardContent className="p-4">
                    <Label className="text-xs mb-2 block">Print Size</Label>
                    <select value={printSize} onChange={(e) => setPrintSize(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
                      {printSizes.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.description}</option>)}
                    </select>
                  </CardContent></Card>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {step < 5 && (
          <div className="flex justify-between mt-8">
            <Button variant="outline" onClick={() => (step === 0 ? navigate('/') : setStep(step - 1))}><ArrowLeft className="w-4 h-4 mr-1" /> {step === 0 ? 'Cancel' : 'Back'}</Button>
            {step < 4 && <Button onClick={() => setStep(step + 1)} disabled={!canNext()} className="bg-teal-600 hover:bg-teal-700">Next <ArrowRight className="w-4 h-4 ml-1" /></Button>}
          </div>
        )}
      </div>
    </>
  );
}
